<script setup>
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  slots: { type: Array, required: true },
});

const emit = defineEmits(['remove', 'addSlot', 'normalize', 'typeChange']);

const HOUR_WIDTH_PX = 80;
const QUARTER_WIDTH_PX = HOUR_WIDTH_PX / 4;
const TRACK_WIDTH_PX = 24 * HOUR_WIDTH_PX;
const SNAP_MINUTES = 15;
const MIN_DURATION = 15;
const DEFAULT_SLOT_DURATION = 60;
const DAY_MINUTES = 24 * 60 - 1;
const QUARTER_COUNT = 24 * 4;
const INPUT_WIDTH_PX = 112;
const INPUT_GAP_PX = 8;
const SLOT_GAP_PX = 2;

const { t } = useI18n();

const QUARTERS = Array.from({ length: QUARTER_COUNT }, (_, index) => index);

const drag = ref(null);
const trackRef = ref(null);
const hoverMinutes = ref(null);
const ignoreTrackClick = ref(false);
const openMenuIndex = ref(null);
const menuStyle = ref({});
const menuPanelRef = ref(null);

onClickOutside(
  menuPanelRef,
  () => {
    openMenuIndex.value = null;
  },
  { ignore: ['[data-slot-menu-trigger]'] }
);

const parseHm = hm => {
  const [h, m] = String(hm || '')
    .split(':')
    .map(n => parseInt(n, 10));
  if (!Number.isFinite(h)) return null;
  return h * 60 + (Number.isFinite(m) ? m : 0);
};

const formatHm = totalMinutes => {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, Math.round(totalMinutes)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const minutesToPx = minutes => (minutes / 60) * HOUR_WIDTH_PX;

const pxToMinutes = px => (px / HOUR_WIDTH_PX) * 60;

const snapMinutes = minutes =>
  Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

const slotLayout = slot => {
  const open = parseHm(slot.time_start);
  const close = parseHm(slot.time_end);
  if (open == null || close == null || close <= open) {
    return null;
  }
  const split = parseHm(slot.reservation_end);
  const width = minutesToPx(close - open);
  const isClosed = Boolean(slot.closed);
  const isSpillover =
    !isClosed &&
    Boolean(slot.reservation_end) &&
    split != null &&
    split <= open;

  return {
    left: minutesToPx(open) + SLOT_GAP_PX / 2,
    width: Math.max(SLOT_GAP_PX, width - SLOT_GAP_PX),
    endLeft: minutesToPx(close),
    isSpillover,
    isClosed,
  };
};

const layouts = computed(() => props.slots.map(slot => slotLayout(slot)));

const sharesStartWithPrev = index => {
  const slot = props.slots[index];
  if (!slot) return false;
  return props.slots.some(
    (other, otherIndex) =>
      otherIndex !== index && other.time_end === slot.time_start
  );
};

const inputLayouts = computed(() => {
  const boxes = layouts.value.flatMap((layout, index) => {
    const startAnchor = layout?.left ?? 0;
    const endAnchor = layout?.endLeft ?? HOUR_WIDTH_PX * 2;
    const wideEnough =
      endAnchor - startAnchor >= 2 * INPUT_WIDTH_PX + INPUT_GAP_PX;
    const inputs = [];
    if (!sharesStartWithPrev(index)) {
      inputs.push({
        index,
        kind: 'start',
        preferred: wideEnough ? startAnchor : startAnchor - INPUT_WIDTH_PX,
      });
    }
    inputs.push({
      index,
      kind: 'end',
      preferred: wideEnough ? endAnchor - INPUT_WIDTH_PX : endAnchor,
    });
    return inputs;
  });

  boxes.sort((a, b) => a.preferred - b.preferred || a.index - b.index);
  boxes.forEach(box => {
    box.left = box.preferred;
  });

  const packForward = () => {
    boxes.forEach((box, i) => {
      if (i === 0) {
        box.left = Math.max(0, box.left);
        return;
      }
      const minLeft = boxes[i - 1].left + INPUT_WIDTH_PX + INPUT_GAP_PX;
      if (box.left < minLeft) box.left = minLeft;
    });
  };

  packForward();
  for (let i = boxes.length - 2; i >= 0; i -= 1) {
    const maxLeft = boxes[i + 1].left - INPUT_WIDTH_PX - INPUT_GAP_PX;
    if (boxes[i].left > maxLeft) boxes[i].left = maxLeft;
  }
  packForward();

  return props.slots.map((_, index) => {
    const start = boxes.find(
      box => box.index === index && box.kind === 'start'
    );
    const end = boxes.find(box => box.index === index && box.kind === 'end');
    return {
      startLeft: start?.left ?? 0,
      endLeft: end?.left ?? HOUR_WIDTH_PX * 2,
    };
  });
});

const neighborsOf = index => {
  const ranges = props.slots
    .map((slot, slotIndex) => ({
      index: slotIndex,
      start: parseHm(slot.time_start),
      end: parseHm(slot.time_end),
      closed: Boolean(slot.closed),
    }))
    .filter(range => range.start != null && range.end != null)
    .sort((a, b) => a.start - b.start);
  const pos = ranges.findIndex(range => range.index === index);
  return {
    prev: pos > 0 ? ranges[pos - 1] : null,
    next: pos >= 0 && pos < ranges.length - 1 ? ranges[pos + 1] : null,
  };
};

const setNeighborEdge = (neighbor, edge, minutes) => {
  if (!neighbor) return;
  const slot = props.slots[neighbor.index];
  const wasSpillover =
    !slot.closed &&
    slot.reservation_end &&
    slot.reservation_end <= slot.time_start;
  if (edge === 'end') slot.time_end = formatHm(minutes);
  else slot.time_start = formatHm(minutes);
  if (wasSpillover) slot.reservation_end = slot.time_start;
};

const setSlotStart = (index, value) => {
  const slot = props.slots[index];
  const oldStart = slot.time_start;
  const wasSpillover =
    !slot.closed &&
    slot.reservation_end &&
    slot.reservation_end <= slot.time_start;
  slot.time_start = value;
  if (wasSpillover) slot.reservation_end = value;
  const prev = props.slots.find(
    (other, otherIndex) => otherIndex !== index && other.time_end === oldStart
  );
  if (prev) prev.time_end = value;
};

const setSlotEnd = (index, value) => {
  const slot = props.slots[index];
  const oldEnd = slot.time_end;
  slot.time_end = value;
  const next = props.slots.find(
    (other, otherIndex) => otherIndex !== index && other.time_start === oldEnd
  );
  if (!next) return;
  const wasSpillover =
    !next.closed &&
    next.reservation_end &&
    next.reservation_end <= next.time_start;
  next.time_start = value;
  if (wasSpillover) next.reservation_end = value;
};

const dragMinStart = prev => {
  if (!prev) return 0;
  return prev.start + MIN_DURATION;
};

const dragMaxEnd = next => {
  if (!next) return DAY_MINUTES;
  return next.end - MIN_DURATION;
};

const isSandwichedClosed = index => {
  if (!props.slots[index]?.closed) return false;
  const { prev, next } = neighborsOf(index);
  return Boolean(prev && next);
};

const slotCursorClass = index => {
  if (!layouts.value[index]) return 'outline outline-1 outline-n-ruby-8';
  if (layouts.value[index].isClosed) return 'cursor-pointer';
  return '';
};

const minutesFromEvent = event => {
  const rect = trackRef.value?.getBoundingClientRect();
  if (!rect) return 0;
  return (
    Math.floor(pxToMinutes(event.clientX - rect.left) / SNAP_MINUTES) *
    SNAP_MINUTES
  );
};

const emitSplitAt = start => {
  if (start == null || start < 0) return;
  emit('addSlot', {
    time_start: formatHm(start),
    time_end: formatHm(Math.min(start + DEFAULT_SLOT_DURATION, DAY_MINUTES)),
  });
};

const updateHover = event => {
  hoverMinutes.value = minutesFromEvent(event);
};

const suppressNextTrackClick = () => {
  ignoreTrackClick.value = true;
  window.setTimeout(() => {
    ignoreTrackClick.value = false;
  }, 0);
};

const onTrackClickCapture = event => {
  if (!ignoreTrackClick.value) return;
  event.preventDefault();
  event.stopPropagation();
};

const onTrackClick = event => {
  if (ignoreTrackClick.value) return;
  emitSplitAt(minutesFromEvent(event));
};

const clearHover = () => {
  hoverMinutes.value = null;
};

const setSlotType = (index, type) => {
  const slot = props.slots[index];
  if (type === 'closed') {
    slot.closed = true;
    slot.reservation_end = '';
  } else if (type === 'spillover') {
    slot.closed = false;
    slot.reservation_end = slot.time_start;
  } else {
    slot.closed = false;
    slot.reservation_end = '';
  }
  emit('typeChange');
};

const slotMenuSections = computed(() => {
  const index = openMenuIndex.value;
  if (index == null) return [];
  const layout = layouts.value[index];
  const sections = [
    {
      items: [
        {
          action: 'setType',
          value: 'bookable',
          label: t('SETTINGS.SPACES.SCHEDULE.BOOKABLE'),
          isSelected: Boolean(
            layout && !layout.isClosed && !layout.isSpillover
          ),
        },
        {
          action: 'setType',
          value: 'spillover',
          label: t('SETTINGS.SPACES.SCHEDULE.SPILLOVER'),
          isSelected: Boolean(layout?.isSpillover),
        },
        {
          action: 'setType',
          value: 'closed',
          label: t('SETTINGS.SPACES.SCHEDULE.CLOSED'),
          isSelected: Boolean(layout?.isClosed),
        },
      ],
    },
  ];
  if (!layout?.isClosed || isSandwichedClosed(index)) {
    sections.push({
      items: [
        {
          action: 'delete',
          value: 'remove',
          label: t('SETTINGS.SPACES.SCHEDULE.REMOVE'),
          icon: 'i-lucide-trash-2',
        },
      ],
    });
  }
  return sections;
});

const onMenuAction = ({ action, value }) => {
  const index = openMenuIndex.value;
  openMenuIndex.value = null;
  if (index == null) return;
  if (action === 'delete') {
    emit('remove', index);
    return;
  }
  if (action === 'setType') setSlotType(index, value);
};

const openSlotMenu = (event, index) => {
  event.preventDefault();
  if (openMenuIndex.value === index) {
    openMenuIndex.value = null;
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  menuStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${Math.max(8, rect.right - 180)}px`,
  };
  openMenuIndex.value = index;
};

const applyReservationEnd = (slot, nextStart, nextEnd, originStart) => {
  if (slot.closed) {
    slot.reservation_end = '';
    return;
  }
  const reservation = parseHm(slot.reservation_end);
  if (reservation == null) return;
  if (reservation <= originStart) {
    slot.reservation_end = formatHm(nextStart);
    return;
  }
  if (reservation <= nextStart || reservation >= nextEnd) {
    slot.reservation_end = '';
    return;
  }
  slot.reservation_end = formatHm(reservation);
};

const applyDrag = deltaMinutes => {
  const state = drag.value;
  if (!state) return;
  const slot = props.slots[state.index];
  const { originStart, originEnd, mode } = state;
  if (originStart == null || originEnd == null) return;
  const { prev, next } = neighborsOf(state.index);

  if (mode === 'start') {
    const nextStart = Math.min(
      originEnd - MIN_DURATION,
      Math.max(dragMinStart(prev), snapMinutes(originStart + deltaMinutes))
    );
    slot.time_start = formatHm(nextStart);
    setNeighborEdge(prev, 'end', nextStart);
    applyReservationEnd(slot, nextStart, originEnd, originStart);
    return;
  }

  const nextEnd = Math.max(
    originStart + MIN_DURATION,
    Math.min(dragMaxEnd(next), snapMinutes(originEnd + deltaMinutes))
  );
  slot.time_end = formatHm(nextEnd);
  setNeighborEdge(next, 'start', nextEnd);
  applyReservationEnd(slot, originStart, nextEnd, originStart);
};

const onPointerDown = (event, index, mode) => {
  event.preventDefault();
  event.stopPropagation();
  openMenuIndex.value = null;
  const slot = props.slots[index];
  drag.value = {
    index,
    mode,
    startX: event.clientX,
    originStart: parseHm(slot.time_start),
    originEnd: parseHm(slot.time_end),
    didDrag: false,
  };
  event.currentTarget.setPointerCapture(event.pointerId);
};

const onPointerMove = event => {
  if (!drag.value) return;
  const deltaPx = event.clientX - drag.value.startX;
  if (Math.abs(deltaPx) > 3) drag.value.didDrag = true;
  applyDrag(snapMinutes(pxToMinutes(deltaPx)));
};

const onPointerUp = () => {
  const state = drag.value;
  drag.value = null;
  if (!state) return;
  suppressNextTrackClick();
  if (state.didDrag) emit('normalize');
};

const onSlotClick = (event, index) => {
  const slot = props.slots[index];
  if (slot.closed && !isSandwichedClosed(index)) {
    emitSplitAt(minutesFromEvent(event));
  }
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      ref="trackRef"
      class="relative h-14 select-none cursor-pointer"
      :style="{ width: `${TRACK_WIDTH_PX}px` }"
      @click.capture="onTrackClickCapture"
      @click="onTrackClick"
      @mousemove="updateHover"
      @mouseleave="clearHover"
    >
      <div class="absolute inset-0 flex pointer-events-none">
        <div
          v-for="quarter in QUARTERS"
          :key="quarter"
          class="h-full shrink-0 border-l last:border-r"
          :class="
            quarter % 4 === 0
              ? 'border-n-slate-4 dark:border-n-slate-6'
              : 'border-n-slate-3 dark:border-n-slate-7'
          "
          :style="{ width: `${QUARTER_WIDTH_PX}px` }"
        />
      </div>
      <div
        v-if="hoverMinutes != null"
        class="absolute inset-y-0 pointer-events-none bg-n-slate-3/80 dark:bg-n-slate-6/80"
        :style="{
          left: `${minutesToPx(hoverMinutes)}px`,
          width: `${QUARTER_WIDTH_PX}px`,
        }"
      />

      <div
        v-for="(slot, index) in slots"
        :key="index"
        class="group absolute top-2 z-10 h-10 rounded-md flex shadow-sm"
        :class="slotCursorClass(index)"
        :style="
          layouts[index]
            ? {
                left: `${layouts[index].left}px`,
                width: `${layouts[index].width}px`,
              }
            : { left: 0, width: `${HOUR_WIDTH_PX * 2}px` }
        "
        @click.stop="onSlotClick($event, index)"
      >
        <button
          type="button"
          class="flex items-center opacity-50 absolute inset-y-0 left-1 z-30 cursor-ew-resize bg-neutral-400/50 hover:bg-neutral-950/75 w-1 p-0 top-1 bottom-1"
          @pointerdown="onPointerDown($event, index, 'start')"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        />
          
        <button
          type="button"
          class="flex items-center opacity-50 absolute inset-y-0 right-1 z-30 cursor-ew-resize bg-neutral-400/50 hover:bg-neutral-950/75 w-1 p-0 top-1 bottom-1"
          @pointerdown="onPointerDown($event, index, 'end')"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        />

        <div
          v-if="layouts[index]?.isClosed"
          class="flex items-center justify-center w-full px-2 pr-10 text-xs font-medium truncate pointer-events-none rounded-md bg-n-ruby-3 text-n-ruby-11"
        >
          {{ t('SETTINGS.SPACES.SCHEDULE.CLOSED') }}
        </div>
        <div
          v-else-if="layouts[index]?.isSpillover"
          class="flex items-center justify-center w-full px-2 pr-10 text-xs font-medium truncate pointer-events-none rounded-md bg-n-blue-3 text-n-blue-11"
        >
          {{ t('SETTINGS.SPACES.SCHEDULE.SPILLOVER') }}
        </div>
        <div
          v-else
          class="flex items-center justify-center w-full px-2 pr-10 text-xs font-medium truncate pointer-events-none rounded-md bg-n-teal-3 text-n-teal-11"
        >
          {{ t('SETTINGS.SPACES.SCHEDULE.BOOKABLE') }}
        </div>

        <button
          type="button"
          data-slot-menu-trigger
          class="absolute top-0.5 bottom-0.5 left-3 z-20 w-8 flex items-center justify-center rounded-md bg-white/90 text-n-slate-11 hover:text-n-slate-12 dark:bg-slate-900/90"
          :aria-label="t('SETTINGS.SPACES.SCHEDULE.SLOT_MENU')"
          @pointerdown="openSlotMenu($event, index)"
          @click.stop
        >
          <Icon icon="i-lucide-ellipsis-vertical shrink-0" class="size-5" />
        </button>
      </div>
    </div>

    <div class="relative h-10" :style="{ width: `${TRACK_WIDTH_PX}px` }">
      <template v-for="(slot, index) in slots" :key="`times-${index}`">
        <div
          v-if="!sharesStartWithPrev(index)"
          class="absolute top-0"
          :style="{ left: `${inputLayouts[index]?.startLeft ?? 0}px` }"
        >
          <Input
            :model-value="slot.time_start"
            type="time"
            size="sm"
            class="w-24 dark:bg-neutral-950 rounded-md"
            @update:model-value="value => setSlotStart(index, value)"
          />
        </div>
        <div
          class="absolute top-0"
          :style="{
            left: `${inputLayouts[index]?.endLeft ?? HOUR_WIDTH_PX * 2}px`,
          }"
        >
          <Input
            :model-value="slot.time_end"
            type="time"
            size="sm"
            class="w-24 dark:bg-neutral-950 rounded-md"
            @update:model-value="value => setSlotEnd(index, value)"
          />
        </div>
      </template>
    </div>

    <Teleport to="body">
      <div
        v-if="openMenuIndex != null"
        ref="menuPanelRef"
        class="fixed z-50"
        :style="menuStyle"
      >
        <DropdownMenu
          :menu-sections="slotMenuSections"
          class="!static min-w-[160px]"
          @action="onMenuAction"
        />
      </div>
    </Teleport>
  </div>
</template>
